import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Newspaper, 
  Clock, 
  Save, 
  RefreshCw,
  Info
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import './FormsUnified.css';

const FIELD_CONFIG = {
  kicker: {
    label: 'Kicker / Eyebrow',
    placeholder: 'e.g. News & Events',
    maxLength: 50
  },
  title: {
    label: 'Main Heading Title',
    placeholder: 'e.g. Latest News & Announcements',
    maxLength: 100
  },
  intro: {
    label: 'Introduction Summary',
    placeholder: 'e.g. Stay updated with our latest news and announcements.',
    maxLength: 300,
    isTextarea: true,
    rows: 3
  },
  latest_news_label: {
    label: 'Card Header Label',
    placeholder: 'e.g. Latest News',
    maxLength: 50
  }
};

export default function NewsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [sectionId, setSectionId] = useState(37);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  // Form values (only the 4 required header fields)
  const [formData, setFormData] = useState({
    kicker: 'News & Updates',
    title: 'Latest News',
    intro: 'Stay updated with our latest news and announcements.',
    latest_news_label: 'Latest News'
  });

  // Pending approvals per field
  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    intro: false,
    latest_news_label: false
  });

  // Track original values
  const [originalData, setOriginalData] = useState({
    kicker: 'News & Updates',
    title: 'Latest News',
    intro: 'Stay updated with our latest news and announcements.',
    latest_news_label: 'Latest News'
  });

  const loadNewsSection = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      let section = null;
      let pending_changes = [];

      // 1. Try with-pending by slug
      let response = await fetch(
        `${apiBase}/cms/section/news/with-pending?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      // 2. Try with-pending by ID
      if (!response.ok) {
        response = await fetch(
          `${apiBase}/cms/section/37/with-pending?_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      // 3. Fallback to homepage sections
      if (!response.ok) {
        response = await fetch(
          `${apiBase}/homepage/cms/sections?published=true&_t=${Date.now()}`,
          { credentials: 'omit' }
        );
      }

      if (response.ok) {
        const result = await response.json();

        if (result.success && result.data) {
          section = result.data.section || result.data;
          pending_changes = result.data.pending_changes || [];
        } else if (result.ok && Array.isArray(result.data)) {
          section = result.data.find(s => s.section_type === 'news' || s.id === 37);
        } else if (Array.isArray(result)) {
          section = result.find(s => s.section_type === 'news' || s.id === 37);
        }
      }

      if (section) {
        let published = section.content_published;
        if (typeof published === 'string') {
          try { published = JSON.parse(published); } catch (e) { published = {}; }
        }

        let draft = section.content;
        if (typeof draft === 'string') {
          try { draft = JSON.parse(draft); } catch (e) { draft = {}; }
        }

        const source = published?.en || published?.id || draft?.en || draft?.id || published || draft || {};

        const initialData = {
          kicker: source.kicker || 'News & Updates',
          title: source.title || 'Latest News',
          intro: source.intro || 'Stay updated with our latest news and announcements.',
          latest_news_label: source.latest_news_label || source.latestNewsLabel || 'Latest News'
        };

        const pendingMap = {
          kicker: false,
          title: false,
          intro: false,
          latest_news_label: false
        };

        // When field change is pending, display proposed/new value
        if (Array.isArray(pending_changes)) {
          pending_changes.forEach(change => {
            if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
              let val = change.new_value;
              if (typeof val === 'string') {
                try { val = JSON.parse(val); } catch (e) {}
              }

              if (change.field_id in pendingMap) {
                pendingMap[change.field_id] = true;
                if (val !== undefined && val !== null && val !== '') {
                  initialData[change.field_id] = val;
                }
              }
            }
          });
        }

        setFormData(initialData);
        setOriginalData({ ...initialData });
        setPendingFields(pendingMap);
        setSectionId(section.id || 37);

        if (isManualRefresh) {
          toastRef.current?.success('Section data refreshed successfully');
        }
      }
    } catch (err) {
      console.warn('[NewsForm] Error loading news section:', err.message);
      if (isManualRefresh) {
        toastRef.current?.error('Failed to refresh: ' + err.message);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadNewsSection();
  }, [loadNewsSection]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toastRef.current?.error('Section ID not loaded');
      return;
    }

    if (formData[fieldName] === originalData[fieldName]) {
      toastRef.current?.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    const config = FIELD_CONFIG[fieldName];
    const fieldDisplayName = config?.label || fieldName.replace(/_/g, ' ');

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldDisplayName,
            old_value: originalData[fieldName],
            new_value: formData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toastRef.current?.success('Change submitted for Principal review');
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409) {
          toastRef.current?.error('This field already has a pending approval');
        } else {
          toastRef.current?.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toastRef.current?.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading news section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Newspaper className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="unified-title">News Section (Homepage)</h1>
            <p className="unified-subtitle">Configure header titles, eyebrow kicker, and card labels displayed on the homepage news panel</p>
          </div>
        </div>
        <div className="unified-header-actions">
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={() => loadNewsSection(true)}
            disabled={refreshing}
            title="Reload data from server"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Informational Sync Notice */}
      <div 
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '14px',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '14px',
          marginBottom: '24px'
        }}
      >
        <div style={{ background: '#2563eb', color: '#fff', borderRadius: '50%', padding: '6px', display: 'flex', flexShrink: 0, marginTop: '2px' }}>
          <Info size={18} />
        </div>
        <div>
          <p style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: 700, color: '#1e3a8a' }}>
            Automatic Synchronization: Latest 3 Articles from News & Updates
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: '#1d4ed8', lineHeight: '1.55' }}>
            The Homepage automatically displays the 3 latest approved articles from the <strong>News & Updates &gt; Blog & Articles</strong> module. Individual news articles do not need to be entered here. To add, edit, or categorize news articles, please manage them directly in the <strong>News & Updates</strong> menu.
          </p>
        </div>
      </div>

      {/* Section Fields Card */}
      <div className="unified-card">
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '20px' }}>
          Section Header Details
        </h3>

        {/* 1. Kicker / Eyebrow */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Kicker / Eyebrow</label>
            {pendingFields.kicker && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength={FIELD_CONFIG.kicker.maxLength}
              className="unified-input"
              value={formData.kicker}
              onChange={(e) => handleFieldChange('kicker', e.target.value)}
              disabled={pendingFields.kicker}
              placeholder={FIELD_CONFIG.kicker.placeholder}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('kicker')}
              disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
            >
              <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* 2. Main Heading Title */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Main Heading Title</label>
            {pendingFields.title && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength={FIELD_CONFIG.title.maxLength}
              className="unified-input"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              disabled={pendingFields.title}
              placeholder={FIELD_CONFIG.title.placeholder}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('title')}
              disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
            >
              <Save size={14} /> {submitting.title ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* 3. Introduction Summary */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Introduction Summary</label>
            {pendingFields.intro && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <textarea
              maxLength={FIELD_CONFIG.intro.maxLength}
              rows={FIELD_CONFIG.intro.rows}
              className="unified-textarea"
              value={formData.intro}
              onChange={(e) => handleFieldChange('intro', e.target.value)}
              disabled={pendingFields.intro}
              placeholder={FIELD_CONFIG.intro.placeholder}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('intro')}
              disabled={submitting.intro || pendingFields.intro || formData.intro === originalData.intro}
            >
              <Save size={14} /> {submitting.intro ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* 4. Card Header Label */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <label className="unified-label">Card Header Label</label>
            {pendingFields.latest_news_label && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              maxLength={FIELD_CONFIG.latest_news_label.maxLength}
              className="unified-input"
              value={formData.latest_news_label}
              onChange={(e) => handleFieldChange('latest_news_label', e.target.value)}
              disabled={pendingFields.latest_news_label}
              placeholder={FIELD_CONFIG.latest_news_label.placeholder}
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('latest_news_label')}
              disabled={submitting.latest_news_label || pendingFields.latest_news_label || formData.latest_news_label === originalData.latest_news_label}
            >
              <Save size={14} /> {submitting.latest_news_label ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
